import {
  Badge,
  Button,
  Container,
  Grid,
  Group,
  Stack,
  Table,
  Text,
  Title,
  ActionIcon,
  Tooltip,
  Alert,
  Modal,
  MultiSelect,
  Select,
  Switch,
} from "@mantine/core";
import {
  IconArrowRight,
  IconEdit,
  IconSparkles,
  IconAlertCircle,
  IconTrash,
  IconChartBar,
} from "@tabler/icons-react";
import { useBrandsStore } from "./brandsStore";
import { useCallback, useEffect, useState } from "react";
import { enrichKeywords, getKeywordMetrics } from "../Keywords/keywordsApi";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import BrandLogo from "../_Shared/BrandLogo";
import { createAutomatedQueries } from "./monitorsApi";
import EditableText from "./EditableText";
import EditableTagsInput from "./EditableTagsInput";
import EditablePriorityArray from "./EditablePriorityArray";
import BrandDiscoveryProgressCard from "./BrandDiscoveryProgressCard";
import TrackingSnippet from "./TrackingSnippet";
import { supportedLanguages } from "../_Shared/LanguageSelector";
import { TARGET_COUNTRIES, countryCodeToFlagEmoji } from "../_Shared/targetMarkets";
import { useStore } from "../store";
import { useLocation } from "wouter";
import { useBrandContext } from "./brandContext";


export default function BrandDetailsSetup({ brand }) {
  const { updateBrand, detectBrandDetails, deleteBrand, refreshBrandById } = useBrandsStore();
  const { fetchBrands: fetchBrandContext } = useBrandContext();
  const [editingField, setEditingField] = useState(null);
  const [isLoadingQueries, setIsLoadingQueries] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [isEnrichingKeywords, setIsEnrichingKeywords] = useState(false);
  const [keywordMetrics, setKeywordMetrics] = useState([]);
  const tenantProfile = useStore((state) => state.tenantProfile);
  const [, setLocation] = useLocation();

  const form = useForm({
    initialValues: brand || {},
    enableReinitialize: true,
  });

  useEffect(() => {
    if (brand) {
      form.setValues(brand);
      // Load cached keyword metrics
      getKeywordMetrics(brand.id)
        .then(setKeywordMetrics)
        .catch(() => {});
    }
  }, [brand]);

  const handleSaveField = useCallback(
    async (fieldName, value) => {
      if (!brand.canEdit) {
        notifications.show({
          title: "Permission Denied",
          message: "You do not have permission to edit this brand",
          color: "red",
        });
        return;
      }

      try {
        const updatedBrand = { ...brand, [fieldName]: value };
        await updateBrand(brand.id, updatedBrand);
        setEditingField(null);
        notifications.show({
          title: "Success",
          message: "Brand updated successfully",
          color: "green",
        });
      } catch (error) {
        notifications.show({
          title: "Error",
          message: "Failed to update brand",
          color: "red",
        });
      }
    },
    [brand, updateBrand]
  );

  const handleEnrichKeywords = useCallback(async () => {
    setIsEnrichingKeywords(true);
    try {
      const result = await enrichKeywords(brand.id);
      setKeywordMetrics(result);
      notifications.show({
        title: "Keywords Enriched",
        message: `Fetched SEO data for ${result.length} keywords`,
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to enrich keywords with SEO data",
        color: "red",
      });
    } finally {
      setIsEnrichingKeywords(false);
    }
  }, [brand]);

  const handleDetectDetails = useCallback(async () => {
    if (!brand.canEdit) {
      notifications.show({
        title: "Permission Denied",
        message: "You do not have permission to edit this brand",
        color: "red",
      });
      return;
    }

    setIsDetecting(true);
    try {
      await detectBrandDetails(brand.id);
    } catch (error) {
      // Error notification is handled in the store
    } finally {
      setIsDetecting(false);
    }
  }, [brand, detectBrandDetails]);

  const handleDeleteBrand = useCallback(async () => {
    if (!brand.canEdit) {
      notifications.show({
        title: "Permission Denied",
        message: "You do not have permission to delete this brand",
        color: "red",
      });
      return;
    }

    setIsDeleting(true);
    try {
      await deleteBrand(brand.id);
      setDeleteModalOpened(false);

      // Refresh brand context to update the dropdown
      await fetchBrandContext();

      // Navigate to brands list after deletion
      setLocation("/brands");
    } catch (error) {
      // Error notification is handled in the store
      setIsDeleting(false);
    }
  }, [brand, deleteBrand, fetchBrandContext, setLocation]);

  if (!brand) {
    return (
      <Container>
        <Text>Loading...</Text>
      </Container>
    );
  }

  const industryOptions = [
    "Technology",
    "Healthcare",
    "Finance",
    "Retail",
    "Manufacturing",
    "Education",
    "Entertainment",
    "Food & Beverage",
    "Automotive",
    "Real Estate",
    "Travel",
    "Sports",
    "Fashion",
    "Beauty",
    "Optics",
  ];

  const canDetect =
    brand.websiteUrl && brand.industries && brand.industries.length > 0;
  const isPreReview = brand.setupStatus === "PreReview";
  const isMonitorSetup = brand.setupStatus === "MonitorSetup";

  const isDiscoveryActive =
    !!brand.currentDiscoveryJobId &&
    brand.discoveryStage !== "Done" &&
    brand.discoveryStage !== "Failed";

  return (
    <>
      <Grid mt="sm">
        <Grid.Col>
          <Stack gap={4} mb="md">
            <Title order={4}>Setup</Title>
            <Text size="sm" c="dimmed">
              Identity, site, industries, competitors, and what we ask AI.
              Writing voice lives in Brand Kit.
            </Text>
          </Stack>
          {(isDiscoveryActive || brand.discoveryStage === "Failed") && (
            <BrandDiscoveryProgressCard
              brand={brand}
              onComplete={() => refreshBrandById(brand.id)}
            />
          )}
          {isPreReview && !isDiscoveryActive && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Brand Setup Incomplete"
              color="yellow"
              mb="md"
            >
              <Text size="sm" mb="md">
                To unlock AI-powered brand detection and analysis, please add:
              </Text>
              <Stack gap="xs" mb="md">
                {!brand.websiteUrl && <Text size="sm">• Website URL</Text>}
                {(!brand.industries || brand.industries.length === 0) && (
                  <Text size="sm">• At least one industry</Text>
                )}
              </Stack>
              {canDetect && (
                <>
                  <Text size="sm" mb="sm">
                    ✓ All requirements met! Click below to detect brand details
                    including competitors, topics, markets, and keywords.
                  </Text>
                  <Button
                    leftSection={<IconSparkles size={16} />}
                    onClick={handleDetectDetails}
                    loading={isDetecting}
                    disabled={!brand.canEdit}
                  >
                    🤖 Detect Brand Details (AI-Powered)
                  </Button>
                </>
              )}
            </Alert>
          )}

          {isMonitorSetup && (
            <Alert
              icon={<IconSparkles size={16} />}
              title="Brand Details Detected!"
              color="green"
              mb="md"
            >
              <Text size="sm" mb="md">
                AI has successfully analyzed your brand. Please review the
                detected competitors, topics, and markets below. You can edit
                them if needed.
              </Text>
              <Text size="sm" mb="md">
                Once you're happy with the details, click{" "}
                <strong>"Set up monitoring"</strong> at the bottom to start
                tracking your brand across LLMs.
              </Text>
            </Alert>
          )}

          <Group my="md" align="center">
            <BrandLogo brand={brand} size={64} />
            <Stack gap={4} align="flex-start">
              <EditableText
                value={brand.name}
                fieldName="name"
                placeholder="Brand name"
                editingField={editingField}
                setEditingField={setEditingField}
                onSave={handleSaveField}
                disabled={!brand.canEdit}
              />
              <EditableTagsInput
                value={brand.industries}
                fieldName="industries"
                editingField={editingField}
                setEditingField={setEditingField}
                onSave={handleSaveField}
                data={industryOptions}
                placeholder="Click to add industries"
                disabled={!brand.canEdit}
              />
            </Stack>
          </Group>

          <Stack align="flex-start" gap={4}>
            <EditableText
              value={brand.websiteUrl}
              fieldName="websiteUrl"
              placeholder="Website URL"
              editingField={editingField}
              setEditingField={setEditingField}
              onSave={handleSaveField}
              disabled={!brand.canEdit}
            />

            <EditableText
              value={brand.descriptionNative}
              fieldName="descriptionNative"
              placeholder="How we recognize you in AI answers. One or two sentences."
              hint="Monitor-facing. Writing voice and value propositions live in Brand Kit."
              multiline
              editingField={editingField}
              setEditingField={setEditingField}
              onSave={handleSaveField}
              disabled={!brand.canEdit}
            />

            <Alert variant="light" color="gray" mt="xs">
              <Text size="sm">
                What only you can say, tone, and glossary live in Brand Kit. That is what every article is written from.
              </Text>
              {brand.valuePropositions && (
                <Text size="sm" c="dimmed" mt={6} lineClamp={3}>
                  {brand.valuePropositions}
                </Text>
              )}
              <Button
                variant="light"
                size="xs"
                mt="sm"
                onClick={() => setLocation("/settings?tab=kit")}
              >
                Open Brand Kit
              </Button>
            </Alert>

            {brand.setupNotes && (
              <Alert
                icon={<IconSparkles size={16} />}
                title="AI Analysis Notes"
                color="blue"
                variant="light"
                mt="md"
                w="100%"
              >
                <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                  {brand.setupNotes}
                </Text>
                {!isPreReview && canDetect && brand.canEdit && (
                  <Button
                    variant="light"
                    size="xs"
                    mt="sm"
                    leftSection={<IconSparkles size={14} />}
                    onClick={handleDetectDetails}
                    loading={isDetecting}
                  >
                    Re-analyze brand (uses credits)
                  </Button>
                )}
              </Alert>
            )}
            {!brand.setupNotes && !isPreReview && canDetect && brand.canEdit && (
              <Button
                variant="light"
                size="sm"
                mt="md"
                leftSection={<IconSparkles size={16} />}
                onClick={handleDetectDetails}
                loading={isDetecting}
              >
                Run AI brand analysis (uses credits)
              </Button>
            )}
          </Stack>
        </Grid.Col>

        <Grid.Col span={6}>
          <EditablePriorityArray
            value={brand.categories}
            fieldName="categories"
            editingField={editingField}
            setEditingField={setEditingField}
            onSave={handleSaveField}
            title="Categories"
            modalTitle="Add/Edit Category"
            nameLabel="Category Name"
            priorityLabel="Priority"
            disabled={!brand.canEdit}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <EditablePriorityArray
            value={brand.topics}
            fieldName="topics"
            editingField={editingField}
            setEditingField={setEditingField}
            onSave={handleSaveField}
            title="Core Aspects"
            modalTitle="Add/Edit Core Aspect"
            nameLabel="Aspect Name"
            priorityLabel="Priority"
            disabled={!brand.canEdit}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <EditablePriorityArray
            value={brand.markets}
            fieldName="markets"
            editingField={editingField}
            setEditingField={setEditingField}
            onSave={handleSaveField}
            title="Markets"
            modalTitle="Add/Edit Market"
            nameLabel="Country Code"
            priorityLabel="Priority"
            renderBadge={(market) =>
              `${market.name} ${countryCodeToFlagEmoji(market.name)}`
            }
            disabled={!brand.canEdit}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Group gap="xs" mb="sm">
            <Title order={3}>Keywords</Title>
            {brand.canEdit && (
              <Tooltip label="Click to edit">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  onClick={() => setEditingField("keywords")}
                >
                  <IconEdit size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
          <EditableTagsInput
            value={brand.keywords}
            fieldName="keywords"
            editingField={editingField}
            setEditingField={setEditingField}
            onSave={handleSaveField}
            placeholder="Click to add keywords"
            hideTitle
            disabled={!brand.canEdit}
          />
          {brand.keywords?.length > 0 && (
            <Button
              variant="light"
              size="xs"
              mt="xs"
              leftSection={<IconChartBar size={14} />}
              onClick={handleEnrichKeywords}
              loading={isEnrichingKeywords}
              disabled={!brand.canEdit}
            >
              Enrich with SEO Data
            </Button>
          )}
          {keywordMetrics.length > 0 && (
            <Table size="xs" mt="sm" striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Keyword</Table.Th>
                  <Table.Th>Volume</Table.Th>
                  <Table.Th>KD</Table.Th>
                  <Table.Th>CPC</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {keywordMetrics
                  .sort((a, b) => (b.searchVolume ?? 0) - (a.searchVolume ?? 0))
                  .map((m) => (
                    <Table.Tr key={m.keyword}>
                      <Table.Td>{m.keyword}</Table.Td>
                      <Table.Td>{m.searchVolume?.toLocaleString() ?? "—"}</Table.Td>
                      <Table.Td>
                        {m.keywordDifficulty != null ? (
                          <Badge
                            size="xs"
                            variant="light"
                            color={m.keywordDifficulty < 30 ? "green" : m.keywordDifficulty < 60 ? "yellow" : "red"}
                          >
                            {Math.round(m.keywordDifficulty)}
                          </Badge>
                        ) : "—"}
                      </Table.Td>
                      <Table.Td>{m.cpc != null ? `$${m.cpc.toFixed(2)}` : "—"}</Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
          )}
        </Grid.Col>

        <Grid.Col span={6}>
          <Title order={3} mb="sm">Home market</Title>
          <Text size="sm" c="dimmed" mb="xs">
            Every prompt is asked from this country, in this language, on every AI surface —
            Google is told where the searcher is, and the chat models are told in the prompt.
            Changing it moves all of them; a prompt with its own market of its own keeps it.
          </Text>
          <Group grow mb="xs">
            <Select
              searchable
              label="Country"
              data={TARGET_COUNTRIES}
              value={brand.defaultTargetCountryCode ?? null}
              onChange={(value) =>
                value && handleSaveField("defaultTargetCountryCode", value)
              }
              disabled={!brand.canEdit}
            />
            <Select
              searchable
              label="Language"
              data={supportedLanguages}
              value={brand.defaultTargetLanguageCode ?? null}
              onChange={(value) =>
                value && handleSaveField("defaultTargetLanguageCode", value)
              }
              disabled={!brand.canEdit}
            />
          </Group>

          <Title order={3} mt="lg" mb="xs">Target Languages</Title>
          <Text size="sm" c="dimmed" mb="xs">
            Extra languages content is translated into. The home language above is the anchor and
            is always included.
          </Text>
          <MultiSelect
            searchable
            placeholder="Select target languages for translation"
            data={supportedLanguages.filter(l => l.value !== brand.defaultTargetLanguageCode)}
            value={brand.targetLanguages || []}
            onChange={(value) => handleSaveField("targetLanguages", value)}
            disabled={!brand.canEdit}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <EditablePriorityArray
            value={brand.competitors}
            fieldName="competitors"
            editingField={editingField}
            setEditingField={setEditingField}
            onSave={handleSaveField}
            title="Competitors"
            modalTitle="Add/Edit Competitor"
            nameLabel="Competitor Name"
            websiteLabel="Website URL"
            priorityLabel="Priority"
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Title order={3} mb="sm">Web Monitoring</Title>
          <Text size="sm" c="dimmed" mb="xs">
            Enable real-time web monitoring to discover mentions of your brand across the internet.
          </Text>
          <Switch
            label="Enable web monitoring"
            checked={brand.firehoseEnabled || false}
            onChange={(e) => handleSaveField("firehoseEnabled", e.currentTarget.checked)}
            disabled={!brand.canEdit}
          />
          {brand.firehoseEnabled && (
            <Select
              label="Match mode"
              description="What to look for in web content"
              data={[
                { value: "brandName", label: "Brand name + domain (default)" },
                { value: "domainOnly", label: "Domain only (for generic brand names)" },
              ]}
              value={brand.firehoseMatchMode || "brandName"}
              onChange={(value) => handleSaveField("firehoseMatchMode", value)}
              disabled={!brand.canEdit}
              mt="xs"
              style={{ maxWidth: 350 }}
            />
          )}
        </Grid.Col>

        <Grid.Col span={12}>
          <TrackingSnippet brand={brand} />
        </Grid.Col>

        <Grid.Col span={12}>
          <Title mt="lg" order={2}>
            Start Monitoring
          </Title>

          {/* <Code block>
          {JSON.stringify(tenantProfile, null, 2)}
          {JSON.stringify(profile, null, 2)}
        </Code> */}

          <Alert>
            Once you are happy with your brand setup, set up monitoring queries
            to track brand mentions, sentiment, and other metrics across various
            LLM providers.
            {isLoadingQueries && (
              <Text size="sm" c="dimmed">
                Setting up monitoring queries... This may take a few minutes.
                You can leave this page and come back later if you wish.
              </Text>
            )}
            <Group mt="xs" justify="flex-end">
              <Button
                onClick={() => {
                  // Get the profile, and the limits -- ensure the user has
                  // a brand left, otherwise send to checkout first
                  if (!tenantProfile?.plan) {
                    notifications.show({
                      title: "Subscription Required",
                      message:
                        "You need to subscribe to a plan to set up monitoring for this brand.",
                      color: "blue",
                    });

                    setLocation("/pricing");
                    return;
                  }

                  if (
                    tenantProfile.usage?.brandsUsed >=
                    tenantProfile.usage?.brandsLimit
                  ) {
                    notifications.show({
                      title: "Upgrade Required",
                      message:
                        "You need to upgrade your plan to set up monitoring for this brand.",
                      color: "blue",
                    });

                    setLocation("/pricing");
                    return;
                  }

                  if (!brand.canAnalyze) {
                    notifications.show({
                      title: "Permission Denied",
                      message:
                        "You do not have permission to set up monitoring for this brand.",
                      color: "red",
                    });
                    return;
                  }

                  setIsLoadingQueries(true);
                  createAutomatedQueries(brand.id)
                    .then((data) => {
                      notifications.show({
                        title: "Monitor Created",
                        message:
                          "Monitoring queries have been created successfully.",
                        color: "green",
                      });
                      updateBrand(brand.id, data);
                    })
                    .catch((error) => {
                      notifications.show({
                        title: "Error",
                        message: "Failed to create monitoring queries.",
                        color: "red",
                      });
                    })
                    .finally(() => {
                      setIsLoadingQueries(false);
                    });
                }}
                loading={isLoadingQueries}
                rightSection={<IconArrowRight />}
                mt="sm"
                disabled={!brand.canAnalyze}
              >
                Set up monitoring
              </Button>
            </Group>
          </Alert>
        </Grid.Col>

        {/* Danger Zone - Delete Brand */}
        <Grid.Col span={12}>
          <Title mt="xl" order={2} c="red">
            Danger Zone
          </Title>

          <Alert color="red" mt="md" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" fw={500} mb="xs">
              Delete this brand
            </Text>
            <Text size="sm" mb="md">
              Once you delete a brand, there is no going back. This will
              permanently delete the brand and all associated data including
              monitoring queries, analytics, and reports.
            </Text>
            <Button
              color="red"
              variant="light"
              leftSection={<IconTrash size={16} />}
              onClick={() => setDeleteModalOpened(true)}
              disabled={!brand.canEdit}
            >
              Delete Brand
            </Button>
          </Alert>
        </Grid.Col>
      </Grid>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        title="Delete Brand"
        centered
      >
        <Stack>
          <Text>
            Are you sure you want to delete <strong>{brand.name}</strong>? This
            action cannot be undone.
          </Text>
          <Text size="sm" c="dimmed">
            All associated data including monitoring queries, analytics, and
            reports will be permanently deleted.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button
              variant="default"
              onClick={() => setDeleteModalOpened(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeleteBrand}
              loading={isDeleting}
              leftSection={<IconTrash size={16} />}
            >
              Delete Brand
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
